import { useEffect, useMemo, useState } from 'react';
import { ChannelType } from '@trii/types/dist/Common/Channels';
import {
  IBusiness,
  IContactField,
  IContactInfo,
  ILabel,
  Property,
} from '@trii/types/dist/Contacts';
import UseEditBusiness from './types/UseEditBusiness';
// Hooks
import useField from '../useField';
import useImage from '../useImage';
import useSelect from '../useSelect';
import useEditContactMethods from '../useEditContactMethods';
import { useMultipleSelect } from '../useMultipleSelect';
// Api
import { deleteAddress } from '../../services/api';

type UseEditBusinessArgs = {
  business: IBusiness | null;
  customContactFields: IContactField[];
  baseUrl?: string;
  spaceId?: string;
  onEntityUpdated?: (entity: unknown) => void;
};

const useEditBusiness = ({
  business,
  customContactFields,
  baseUrl,
  spaceId,
  onEntityUpdated,
}: UseEditBusinessArgs): UseEditBusiness => {
  const apiDeleteAddress = useMemo(
    () => async (payload: { contactId: string; contactAddressId: string; type: ChannelType }) => {
      if (!baseUrl || !spaceId) return;
      await deleteAddress({ baseUrl, spaceId, data: payload });
    },
    [baseUrl, spaceId],
  );

  const apiDispatch = async <T>(maybePromise: Promise<T> | T) => maybePromise;

  const [customProperties, setCustomProperties] = useState<Property[]>([]);
  // General section fields
  const name = useField('text', business?.name || '');
  const contactImage = useImage({
    baseUrl,
    spaceId,
    entityType: 'business',
    entityId: business?.id,
    initialImageUrl: business?.imageUrl || '',
  });
  const contactPropietary = useSelect(business?.owner);
  const labelMultipleSelect = useMultipleSelect<ILabel[]>({
    initialState: (business?.tags as ILabel[]) ?? [],
  });
  const [selectedContacts, setSelectedContacts] = useState<IContactInfo[]>([]);

  const {
    field: {
      imsFacebooks,
      imsMercadolibre,
      imsWebchats,
      imsInstagrams,
      imsWhatsapp,
      emails,
      phoneNumbers,
      imsRCS,
    },
    action: {
      addPhone,
      addEmail,
      addWhatsapp,
      addRCS,
      addWhatsappVerified,
      replaceAddress,
      handleAddressFieldChange,
      handleNoteFieldChange,
      handleDeleteAddress,
      resetToInitialContactInformation,
    },
    state: { isDeletingAddress },
  } = useEditContactMethods({
    entityId: business?.id,
    initial: business,
    dispatch: apiDispatch,
    deleteAddress: apiDeleteAddress,
    onEntityUpdated,
  });

  // Address iformation fields
  const homeAddress = useField('text', business?.address1?.street || '');
  const secondaryHomeAddress = useField('text', business?.address2?.street || '');
  const city = useField('text', business?.address1?.city || '');
  const secondaryCity = useField('text', business?.address2?.city || '');
  const state = useField('text', business?.address1?.state || '');
  const secondaryState = useField('text', business?.address2?.state || '');
  const postalCode = useField('text', business?.address1?.zipcode || '');
  const secondaryPostalCode = useField('text', business?.address2?.zipcode || '');
  const country = useField('text', business?.address1?.country || '');
  const secondaryCountry = useField('text', business?.address2?.country || '');

  const getEditedField = (
    key: string,
    originalValue: string | null | undefined,
    editedValue: string | null | undefined,
  ) => {
    if (originalValue !== editedValue && !(originalValue === null && editedValue === '')) {
      return { [key]: editedValue };
    }
    return {};
  };
  // Actions
  const getEditedBusiness = () => {
    if (!business) return null;

    const address1 = {
      street: homeAddress.attributes.value,
      city: city.attributes.value,
      state: state.attributes.value,
      zipcode: postalCode.attributes.value,
      country: country.attributes.value,
    };
    const address2 = {
      street: secondaryHomeAddress.attributes.value,
      city: secondaryCity.attributes.value,
      state: secondaryState.attributes.value,
      zipcode: secondaryPostalCode.attributes.value,
      country: secondaryCountry.attributes.value,
    };
    const editedBusiness = {
      id: business.id,
      ...getEditedField('owner', business.owner, contactPropietary.attributes.value),
      ...getEditedField('name', business.name, name.attributes.value),
      imageUrl: contactImage.imageUrl ? contactImage.removeParams(contactImage.imageUrl) : null,
      tags: labelMultipleSelect.attributes.value,
      properties: customProperties,
      members: selectedContacts,
      phoneNumbers,
      emails,
      address1,
      address2,
      // ims_whatsapp: modifiedImsWhatsapp,
    };
    return editedBusiness;
  };

  function resetCustomProperty(nameKey: string) {
    if (!business) return;

    const normalizeNameKey = (input: string) =>
      input.trim().toLowerCase().replace(/\s+/g, '-').replace(/-+/g, '-');

    const defaultProperty =
      business.properties.find((property) => property.nameKey === nameKey) ??
      business.properties.find(
        (property) => normalizeNameKey(property.nameKey) === normalizeNameKey(nameKey),
      );

    if (!defaultProperty) {
      console.error(`No se encontró una propiedad con nameKey: ${nameKey}`);
      return; // Salir de la función si no se encuentra la propiedad
    }

    const updatedProperties = customProperties.map((property) =>
      property.nameKey === nameKey ? { ...property, value: defaultProperty.value } : property,
    );
    setCustomProperties(updatedProperties);
  }

  const getFilteredCustomProperties = (): Property[] => {
    if (!business) return [];

    return customContactFields.map((customField) => {
      const normalizeNameKey = (input: string) =>
        input.trim().toLowerCase().replace(/\s+/g, '-').replace(/-+/g, '-');

      const existingBusinessProperty =
        business.properties.find((property) => property.nameKey === customField.nameKey) ??
        business.properties.find(
          (property) =>
            normalizeNameKey(property.nameKey) === normalizeNameKey(customField.nameKey),
        );

      if (existingBusinessProperty) {
        return {
          ...existingBusinessProperty,
          nameKey: customField.nameKey,
          type: (existingBusinessProperty.type ?? customField.type)!,
          value: existingBusinessProperty.value,
        };
      }

      const existingLocalProperty = customProperties.find(
        (property) => property.nameKey === customField.nameKey,
      );

      return {
        nameKey: customField.nameKey,
        type: customField.type!,
        value: existingLocalProperty ? existingLocalProperty.value : '',
      };
    });
  };

  useEffect(() => {
    if (business && customContactFields) {
      setCustomProperties(getFilteredCustomProperties());
    }
  }, [business, customContactFields]);

  useEffect(() => {
    if (!business) return;

    labelMultipleSelect.actions.setValue((business.tags as ILabel[]) ?? []);
  }, [business]);

  return {
    field: {
      name,
      imsWhatsapp,
      imsRCS,
      contactImage,
      contactPropietary,
      imsMercadolibre,
      imsFacebooks,
      imsWebchats,
      imsInstagrams,
      emails,
      phoneNumbers,
      homeAddress,
      secondaryHomeAddress,
      city,
      secondaryCity,
      state,
      secondaryState,
      postalCode,
      secondaryPostalCode,
      country,
      secondaryCountry,
      customProperties,
      labelMultipleSelect,
    },
    action: {
      setCustomProperties,
      addWhatsapp,
      addRCS,
      getEditedBusiness,
      handleAddressFieldChange,
      handleNoteFieldChange,
      handleDeleteAddress,
      addPhone,
      addEmail,
      replaceAddress,
      addWhatsappVerified,
      resetToInitialContactInformation,
      resetCustomProperty,
    },
    state: {
      isDeletingAddress,
    },
    selectedContacts,
    setSelectedContacts,
  };
};

export default useEditBusiness;
